import type { JSX } from 'react';

import { Button } from '@change/ds-core-ui/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Input } from '@change-corgi/design-system-legacy/components/forms';
import { Box } from '@change-corgi/design-system-legacy/layout';

import type { PetitionEditPageState } from 'src/app/pages/petitionEditV2/pageContext';

import { useEditDecisionMakerForm } from './hooks/useEditDecisionMakerForm';

type DecisionMakerType = PetitionEditPageState['formState']['decisionMakers']['value'][number];
type ViewMode = 'EDITING' | 'READONLY' | 'CREATING';

type Props = {
	decisionMaker: DecisionMakerType;
	setReadonly: () => void;
	onRemoveDecisionMaker: () => void;
	mode: ViewMode;
};
export function EditDecisionMakerForm({ decisionMaker, setReadonly, onRemoveDecisionMaker, mode }: Props): JSX.Element {
	const { data, actions } = useEditDecisionMakerForm({ decisionMaker, setReadonly, mode, onRemoveDecisionMaker });
	const { translate } = useI18n();
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', p: 24, gap: 16 }}>
			<Input
				id="dm-name"
				label={translate('corgi.petition.edit.petition_data.decision_maker.dm_name')}
				type="text"
				size="large"
				error={data.nameError}
				defaultValue={decisionMaker.displayName}
				onChange={actions.handleOnNameChange}
				ref={data.nameInputRef}
				data-qa="dm-name-input"
			/>
			<Input
				id="dm-summary"
				label={translate('corgi.petition.edit.petition_data.decision_maker.dm_info')}
				type="text"
				size="large"
				defaultValue={decisionMaker.title ?? ''}
				ref={data.summaryInputRef}
				data-qa="dm-summary-input"
			/>
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: 16, justifyContent: 'center' }}>
				<Button
					className="w-full"
					size="small"
					variant="secondary"
					data-qa="dm-cancel-button"
					onClick={actions.handleOnCancel}
				>
					<Translate value="corgi.petition.edit.petition_data.decision_maker.cancel" />
				</Button>
				<Button
					className="w-full"
					size="small"
					type="submit"
					variant="primary"
					data-qa="dm-save-button"
					onClick={actions.handleOnSave}
				>
					<Translate
						value={`corgi.petition.edit.petition_data.decision_maker.${mode === 'EDITING' ? 'save' : 'create'}`}
					/>
				</Button>
			</Box>
		</Box>
	);
}
